import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  bunkerLogin,
  ncryptsecLogin,
  nip07Login,
  nostrConnectionLogin,
  npubLogin,
  nsecLogin
} from '@/atoms/session'

import { googleLogin, TRY_AGAIN } from '@/services/google-login.service'
import { generateSecretKey, getPublicKey } from '@nostr/tools/wasm'
import { createNostrConnectURI, NostrConnectParams } from '@nostr/tools/nip46'
import { nsecEncode } from '@nostr/tools/nip19'
import { HugeiconsIcon } from '@hugeicons/react'
import { CheckIcon, Copy01Icon, Loading03Icon } from '@hugeicons/core-free-icons'
import { useRef, useState, useEffect, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QrCode from '../QrCode'
import { useAsync } from 'react-use'

export default function AccountManager({ close }: { close?: () => void }) {
  const { t } = useTranslation()

  const centralURL = useRef(window.hallway.universe.pomegranateCentral)

  const [input, setInput] = useState('')
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [googlePending, setGooglePending] = useState(false)

  const { value: hasNip07 } = useAsync(async () => {
    await new Promise((resolve) => setTimeout(resolve, 600))
    return typeof window !== 'undefined' && !!window.nostr
  }, [])

  const [loginDetails] = useState(() => {
    const newPrivKey = generateSecretKey()
    const newMeta: NostrConnectParams = {
      clientPubkey: getPublicKey(newPrivKey),
      relays: window.hallway.universe.defaultNostrConnectRelays,
      secret: Math.random().toString(36).substring(7),
      name: document.location.host,
      url: document.location.origin
    }
    return {
      privKey: newPrivKey,
      connectionString: createNostrConnectURI(newMeta)
    }
  })
  const [copied, setCopied] = useState(false)
  const [ncErr, setNcErr] = useState<string | null>(null)
  const qrRef = useRef<HTMLDivElement>(null)
  const [qrSize, setQrSize] = useState(200)

  useLayoutEffect(() => {
    const update = () => {
      if (qrRef.current) {
        const w = qrRef.current.offsetWidth
        setQrSize(Math.max(140, Math.min(w - 16, 260)))
      }
    }
    update()
    const ro = new ResizeObserver(update)
    if (qrRef.current) ro.observe(qrRef.current)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (!loginDetails.privKey || !loginDetails.connectionString) return
    setNcErr(null)
    nostrConnectionLogin(loginDetails.privKey, loginDetails.connectionString)
      .then(() => close?.())
      .catch((err) =>
        setNcErr(
          err.message ? `${err.message}. Please reload.` : 'Connection failed. Please reload.'
        )
      )
  }, [loginDetails])

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-6">
      {hasNip07 && (
        <>
          <Button
            onClick={handleNip07Login}
            disabled={pending}
            className="w-full h-14 text-lg font-bold"
          >
            {pending && (
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin mr-2" size={18} />
            )}
            {t('Connect with Browser Extension')}
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex-grow border-t border-border/40" />
            <span className="text-xs text-muted-foreground">OR</span>
            <div className="flex-grow border-t border-border/40" />
          </div>
        </>
      )}

      {centralURL.current && (
        <Button onClick={handleGoogleLogin} disabled={googlePending} className="w-full">
          {googlePending && (
            <HugeiconsIcon icon={Loading03Icon} className="animate-spin mr-2" size={16} />
          )}
          {t('Connect with Google')}
          <svg
            role="img"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
          >
            <title>Google</title>
            <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"></path>
          </svg>
        </Button>
      )}

      {centralURL.current && (
        <div className="flex items-center gap-2">
          <div className="flex-grow border-t border-border/40" />
          <span className="text-xs text-muted-foreground">OR</span>
          <div className="flex-grow border-t border-border/40" />
        </div>
      )}

      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            placeholder={t('nsec, ncryptsec, bunker://...')}
            value={input}
            onChange={handleInputChange}
            className={errMsg ? 'border-destructive flex-1' : 'flex-1'}
          />
          <Button variant="secondary" onClick={handleGenerate} className="shrink-0">
            {t('Generate Identity')}
          </Button>
        </div>
        {errMsg && <div className="text-xs text-destructive">{errMsg}</div>}
        <Button onClick={handleLogin} disabled={pending || !input.trim()} className="w-full">
          {pending && (
            <HugeiconsIcon icon={Loading03Icon} className="animate-spin mr-2" size={16} />
          )}
          {t('Login')}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-grow border-t border-border/40" />
        <span className="text-xs text-muted-foreground">OR</span>
        <div className="flex-grow border-t border-border/40" />
      </div>

      <div className="space-y-3">
        <div ref={qrRef} className="flex justify-center">
          <a href={loginDetails.connectionString} aria-label="Open with Nostr signer app">
            <QrCode size={qrSize} value={loginDetails.connectionString} />
          </a>
        </div>
        {ncErr && <div className="text-xs text-destructive text-center">{ncErr}</div>}
        <div
          className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-2 rounded-full cursor-pointer mx-auto max-w-[280px] transition-colors hover:bg-muted/80"
          onClick={copyConnectionString}
          role="button"
          tabIndex={0}
        >
          <span className="truncate">{loginDetails.connectionString}</span>
          {copied ? (
            <HugeiconsIcon icon={CheckIcon} size={12} />
          ) : (
            <HugeiconsIcon icon={Copy01Icon} size={12} />
          )}
        </div>
      </div>
    </div>
  )

  function handleNip07Login() {
    setPending(true)
    nip07Login()
      .then(() => close?.())
      .catch(() => {})
      .finally(() => setPending(false))
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
    setErrMsg(null)
  }

  function handleLogin() {
    const val = input.trim()
    if (!val) return
    setPending(true)
    let p: Promise<unknown>
    if (val.startsWith('ncryptsec1')) {
      p = ncryptsecLogin(val)
    } else if (val.startsWith('bunker://') || val.startsWith('bunker:')) {
      p = bunkerLogin(val)
    } else if (val.startsWith('npub1')) {
      p = npubLogin(val)
    } else {
      p = nsecLogin(val, '')
    }
    p.then(() => close?.())
      .catch((err) => setErrMsg(err?.message || 'Login failed'))
      .finally(() => setPending(false))
  }

  function handleGenerate() {
    setInput(nsecEncode(generateSecretKey()))
    setErrMsg(null)
  }

  async function handleGoogleLogin() {
    setGooglePending(true)
    close?.()
    try {
      const bunkerUrl = await googleLogin(centralURL.current!)
      if (bunkerUrl) await bunkerLogin(bunkerUrl)
    } catch (err: any) {
      if (err.type === TRY_AGAIN) {
        centralURL.current = err.central
        return
      }
      console.error('Google login failed', err)
    } finally {
      setGooglePending(false)
    }
  }

  function copyConnectionString() {
    navigator.clipboard.writeText(loginDetails.connectionString)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
}
